// src/data/quran.ts
export interface QuranAyah {
  number: number;
  text: string;
  numberInSurah: number;
  arabicTranslation?: string;
  englishTranslation?: string;
  audioUrl?: string;
}

export interface QuranSurah {
  number: number;
  name: string;
  transliteration: string;
  englishNameTranslation: string;
  revelationType: string;
  ayahs: QuranAyah[];
  count: number;
}

// ✅ Arabic Names Map
function getSurahArabicName(surahNumber: number): string {
  const arabicNames: { [key: number]: string } = {
    1: "الفاتحة", 2: "البقرة", 3: "آل عمران", 4: "النساء", 5: "المائدة",
    6: "الأنعام", 7: "الأعراف", 8: "الأنفال", 9: "التوبة", 10: "يونس",
    11: "هود", 12: "يوسف", 13: "الرعد", 14: "إبراهيم", 15: "الحجر",
    16: "النحل", 17: "الإسراء", 18: "الكهف", 19: "مريم", 20: "طه",
    21: "الأنبياء", 22: "الحج", 23: "المؤمنون", 24: "النور", 25: "الفرقان",
    26: "الشعراء", 27: "النمل", 28: "القصص", 29: "العنكبوت", 30: "الروم",
    31: "لقمان", 32: "السجدة", 33: "الأحزاب", 34: "سبأ", 35: "فاطر",
    36: "يس", 37: "الصافات", 38: "ص", 39: "الزمر", 40: "غافر",
    41: "فصلت", 42: "الشورى", 43: "الزخرف", 44: "الدخان", 45: "الجاثية",
    46: "الأحقاف", 47: "محمد", 48: "الفتح", 49: "الحجرات", 50: "ق",
    51: "الذاريات", 52: "الطور", 53: "النجم", 54: "القمر", 55: "الرحمن",
    56: "الواقعة", 57: "الحديد", 58: "المجادلة", 59: "الحشر", 60: "الممتحنة",
    61: "الصف", 62: "الجمعة", 63: "المنافقون", 64: "التغابن", 65: "الطلاق",
    66: "التحريم", 67: "الملك", 68: "القلم", 69: "الحاقة", 70: "المعارج",
    71: "نوح", 72: "الجن", 73: "المزمل", 74: "المدثر", 75: "القيامة",
    76: "الإنسان", 77: "المرسلات", 78: "النبأ", 79: "النازعات", 80: "عبس",
    81: "التكوير", 82: "الإنفطار", 83: "المطففين", 84: "الإنشقاق", 85: "البروج",
    86: "الطارق", 87: "الأعلى", 88: "الغاشية", 89: "الفجر", 90: "البلد",
    91: "الشمس", 92: "الليل", 93: "الضحى", 94: "الشرح", 95: "التين",
    96: "العلق", 97: "القدر", 98: "البينة", 99: "الزلزلة", 100: "العاديات",
    101: "القارعة", 102: "التكاثر", 103: "العصر", 104: "الهمزة", 105: "الفيل",
    106: "قريش", 107: "الماعون", 108: "الكوثر", 109: "الكافرون", 110: "النصر",
    111: "المسد", 112: "الإخلاص", 113: "الفلق", 114: "الناس"
  };
  return arabicNames[surahNumber] || `سورة ${surahNumber}`;
}

// ✅ Load a single Surah with translations and audio
export async function loadSurah(surahNumber: number): Promise<QuranSurah> {
  try {
    // Load main surah data
    const surahResponse = await fetch(`/data/quran/surah/surah_${surahNumber}.json`);
    if (!surahResponse.ok) throw new Error(`Failed to load Surah ${surahNumber}`);
    const surahData = await surahResponse.json();

    // Load Arabic translation
    let arabicTranslation = null;
    try {
      const arabicResponse = await fetch(`/data/quran/translation/ar/ar_translation_${surahNumber}.json`);
      if (arabicResponse.ok) arabicTranslation = await arabicResponse.json();
    } catch (error) {
      console.log(`Arabic translation not available for Surah ${surahNumber}`);
    }

    // Load English translation
    let englishTranslation = null;
    try {
      const englishResponse = await fetch(`/data/quran/translation/en/en_translation_${surahNumber}.json`);
      if (englishResponse.ok) englishTranslation = await englishResponse.json();
    } catch (error) {
      console.log(`English translation not available for Surah ${surahNumber}`);
    }

    // Parse verses from your structure
    const ayahs: QuranAyah[] = [];
    const verseKeys = Object.keys(surahData.verse || {});
    
    for (let i = 0; i < verseKeys.length; i++) {
      const key = verseKeys[i];
      const verseNumber = i + 1;
      
      // Get Arabic translation for this verse
      const arabicVerse = arabicTranslation?.verse?.[key] || '';
      
      // Get English translation for this verse  
      const englishVerse = englishTranslation?.verse?.[key] || '';

      // Generate correct audio URL based on your structure
      const audioUrl = generateAudioUrl(surahNumber, verseNumber);

      ayahs.push({
        number: verseNumber,
        text: surahData.verse[key],
        numberInSurah: verseNumber,
        arabicTranslation: arabicVerse,
        englishTranslation: englishVerse,
        audioUrl: audioUrl
      });
    }

    const transliteration = getTransliterationByNumber(surahNumber);
    const englishNameTranslation = getSurahTranslation(transliteration);

    return {
      number: parseInt(surahData.index) || surahNumber,
      name: getSurahArabicName(surahNumber),
      transliteration,
      englishNameTranslation,
      revelationType: getRevelationType(surahNumber),
      ayahs,
      count: surahData.count || ayahs.length
    };

  } catch (error) {
    console.error(`Error loading Surah ${surahNumber}:`, error);
    return getFallbackSurah(surahNumber);
  }
}

// ✅ Generate correct audio URL based on your folder structure
function generateAudioUrl(surahNumber: number, verseNumber: number): string {
  const surahFolder = surahNumber.toString().padStart(3, '0');
  
  // For surah 1: 001/001.mp3 to 001/007.mp3
  // For other surahs: 002/000.mp3 to 002/286.mp3 (with leading zeros)
  let audioFileName = '';
  
  if (surahNumber === 1) {
    // Surah 1 has files 001.mp3 to 007.mp3
    audioFileName = verseNumber.toString().padStart(3, '0') + '.mp3';
  } else {
    // Other surahs have files 000.mp3 to [number of ayahs].mp3
    audioFileName = (verseNumber - 1).toString().padStart(3, '0') + '.mp3';
  }
  
  return `/data/quran/audio/${surahFolder}/${audioFileName}`;
}

// Fallback surah data
function getFallbackSurah(surahNumber: number): QuranSurah {
  const transliteration = getTransliterationByNumber(surahNumber);
  const englishTranslation = getSurahTranslation(transliteration);
  
  return {
    number: surahNumber,
    name: getSurahArabicName(surahNumber),
    transliteration,
    englishNameTranslation: englishTranslation,
    revelationType: getRevelationType(surahNumber),
    ayahs: [{
      number: 1,
      text: "بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ",
      numberInSurah: 1,
      audioUrl: generateAudioUrl(surahNumber, 1)
    }],
    count: 1
  };
}

// ✅ Load all Surahs
export async function loadAllSurahs(): Promise<QuranSurah[]> {
  const surahs: QuranSurah[] = [];
  
  // Load all 114 surahs
  for (let surahNumber = 1; surahNumber <= 114; surahNumber++) {
    try {
      const surah = await loadSurah(surahNumber);
      surahs.push(surah);
      console.log(`✅ Loaded Surah ${surahNumber}: ${surah.transliteration}`);
    } catch (error) {
      console.error(`❌ Failed to load Surah ${surahNumber}:`, error);
      // Add fallback surah to ensure the app doesn't break
      surahs.push(getFallbackSurah(surahNumber));
    }
    
    // Small delay to avoid overwhelming
    await new Promise(resolve => setTimeout(resolve, 10));
  }
  
  return surahs.sort((a, b) => a.number - b.number);
}

// ✅ Transliteration Map (for all 114 surahs)
function getTransliterationByNumber(num: number): string {
  const names = [
    "Al-Fatihah", "Al-Baqara", "Aal-Imran", "An-Nisaa", "Al-Ma'idah", "Al-An'am", "Al-A'raf", "Al-Anfal", "At-Tawbah", "Yunus",
    "Hud", "Yusuf", "Ar-Ra'd", "Ibrahim", "Al-Hijr", "An-Nahl", "Al-Isra", "Al-Kahf", "Maryam", "Ta-Ha",
    "Al-Anbiya", "Al-Hajj", "Al-Mu'minun", "An-Nur", "Al-Furqan", "Ash-Shu'ara", "An-Naml", "Al-Qasas", "Al-Ankabut", "Ar-Rum",
    "Luqman", "As-Sajdah", "Al-Ahzab", "Saba", "Fatir", "Ya-Sin", "As-Saffat", "Sad", "Az-Zumar", "Ghafir",
    "Fussilat", "Ash-Shura", "Az-Zukhruf", "Ad-Dukhan", "Al-Jathiya", "Al-Ahqaf", "Muhammad", "Al-Fath", "Al-Hujurat", "Qaf",
    "Adh-Dhariyat", "At-Tur", "An-Najm", "Al-Qamar", "Ar-Rahman", "Al-Waqi'ah", "Al-Hadid", "Al-Mujadilah", "Al-Hashr", "Al-Mumtahinah",
    "As-Saff", "Al-Jumu'ah", "Al-Munafiqun", "At-Taghabun", "At-Talaq", "At-Tahrim", "Al-Mulk", "Al-Qalam", "Al-Haqqah", "Al-Ma'arij",
    "Nuh", "Al-Jinn", "Al-Muzzammil", "Al-Muddaththir", "Al-Qiyamah", "Al-Insan", "Al-Mursalat", "An-Naba", "An-Nazi'at", "Abasa",
    "At-Takwir", "Al-Infitar", "Al-Mutaffifin", "Al-Inshiqaq", "Al-Buruj", "At-Tariq", "Al-A'la", "Al-Ghashiyah", "Al-Fajr", "Al-Balad",
    "Ash-Shams", "Al-Lail", "Ad-Duha", "Ash-Sharh", "At-Tin", "Al-Alaq", "Al-Qadr", "Al-Bayyinah", "Az-Zalzalah", "Al-Adiyat",
    "Al-Qariah", "At-Takathur", "Al-Asr", "Al-Humazah", "Al-Fil", "Quraish", "Al-Ma'un", "Al-Kawthar", "Al-Kafirun", "An-Nasr",
    "Al-Masad", "Al-Ikhlas", "Al-Falaq", "An-Nas"
  ];
  return names[num - 1] || `Surah ${num}`;
}

// ✅ English Translations for all 114 Surahs
function getSurahTranslation(name: string): string {
  const translations: { [key: string]: string } = {
    "Al-Fatihah": "The Opening", "Al-Baqara": "The Cow", "Aal-Imran": "The Family of Imran",
    "An-Nisaa": "The Women", "Al-Ma'idah": "The Table Spread", "Al-An'am": "The Cattle",
    "Al-A'raf": "The Heights", "Al-Anfal": "The Spoils of War", "At-Tawbah": "The Repentance",
    "Yunus": "Jonah", "Hud": "Hud", "Yusuf": "Joseph", "Ar-Ra'd": "The Thunder", "Ibrahim": "Abraham",
    "Al-Hijr": "The Rocky Tract", "An-Nahl": "The Bee", "Al-Isra": "The Night Journey", "Al-Kahf": "The Cave",
    "Maryam": "Mary", "Ta-Ha": "Taa-Haa", "Al-Anbiya": "The Prophets", "Al-Hajj": "The Pilgrimage",
    "Al-Mu'minun": "The Believers", "An-Nur": "The Light", "Al-Furqan": "The Criterion", "Ash-Shu'ara": "The Poets",
    "An-Naml": "The Ant", "Al-Qasas": "The Stories", "Al-Ankabut": "The Spider", "Ar-Rum": "The Romans",
    "Luqman": "Luqman", "As-Sajdah": "The Prostration", "Al-Ahzab": "The Confederates", "Saba": "Sheba",
    "Fatir": "The Originator", "Ya-Sin": "Yaseen", "As-Saffat": "Those Drawn Up in Ranks", "Sad": "The Letter Saad",
    "Az-Zumar": "The Groups", "Ghafir": "The Forgiver", "Fussilat": "Explained in Detail", "Ash-Shura": "Consultation",
    "Az-Zukhruf": "Ornaments of Gold", "Ad-Dukhan": "The Smoke", "Al-Jathiya": "The Crouching", "Al-Ahqaf": "The Dunes",
    "Muhammad": "Muhammad", "Al-Fath": "The Victory", "Al-Hujurat": "The Inner Apartments", "Qaf": "The Letter Qaf",
    "Adh-Dhariyat": "The Winnowing Winds", "At-Tur": "The Mount", "An-Najm": "The Star", "Al-Qamar": "The Moon",
    "Ar-Rahman": "The Most Merciful", "Al-Waqi'ah": "The Inevitable", "Al-Hadid": "The Iron",
    "Al-Mujadilah": "The Pleading Woman", "Al-Hashr": "The Exile", "Al-Mumtahinah": "She That Is To Be Examined",
    "As-Saff": "The Ranks", "Al-Jumu'ah": "Friday", "Al-Munafiqun": "The Hypocrites", "At-Taghabun": "Mutual Disillusion",
    "At-Talaq": "Divorce", "At-Tahrim": "The Prohibition", "Al-Mulk": "The Sovereignty", "Al-Qalam": "The Pen",
    "Al-Haqqah": "The Reality", "Al-Ma'arij": "The Ascending Stairways", "Nuh": "Noah", "Al-Jinn": "The Jinn",
    "Al-Muzzammil": "The Enshrouded One", "Al-Muddaththir": "The Cloaked One", "Al-Qiyamah": "The Resurrection",
    "Al-Insan": "Man", "Al-Mursalat": "Those Sent Forth", "An-Naba": "The Announcement", "An-Nazi'at": "Those Who Pull Out",
    "Abasa": "He Frowned", "At-Takwir": "The Overthrowing", "Al-Infitar": "The Cleaving", "Al-Mutaffifin": "Defrauding",
    "Al-Inshiqaq": "The Splitting Open", "Al-Buruj": "The Constellations", "At-Tariq": "The Morning Star",
    "Al-A'la": "The Most High", "Al-Ghashiyah": "The Overwhelming", "Al-Fajr": "The Dawn", "Al-Balad": "The City",
    "Ash-Shams": "The Sun", "Al-Lail": "The Night", "Ad-Duha": "The Morning Hours", "Ash-Sharh": "The Relief",
    "At-Tin": "The Fig", "Al-Alaq": "The Clot", "Al-Qadr": "The Power", "Al-Bayyinah": "The Clear Proof",
    "Az-Zalzalah": "The Earthquake", "Al-Adiyat": "The Courser", "Al-Qariah": "The Calamity", "At-Takathur": "Rivalry in Worldly Increase",
    "Al-Asr": "The Declining Day", "Al-Humazah": "The Traducer", "Al-Fil": "The Elephant", "Quraish": "Quraysh",
    "Al-Ma'un": "Small Kindnesses", "Al-Kawthar": "Abundance", "Al-Kafirun": "The Disbelievers", "An-Nasr": "Divine Support",
    "Al-Masad": "The Palm Fibre", "Al-Ikhlas": "Sincerity", "Al-Falaq": "The Daybreak", "An-Nas": "Mankind"
  };
  return translations[name] || name;
}

// ✅ Revelation type
function getRevelationType(surahNumber: number): string {
  const meccan = [
    1, 6, 7, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 23, 25, 26, 27, 28, 29,
    30, 31, 32, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 50, 51, 52, 53,
    54, 55, 56, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79, 80, 81, 82, 83,
    84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97, 98, 99, 100, 101, 102,
    103, 104, 105, 106, 107, 108, 109, 110, 111, 112, 113, 114,
  ];
  return meccan.includes(surahNumber) ? "Meccan" : "Medinan";
}